"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import type { AuthActionResult, SocialProvider } from "@/hooks/useFirebaseAuth";
type AuthPanelProps = {
  backendAwake: boolean;
  isBusy: boolean;
  onSocialSignIn: (provider: SocialProvider) => Promise<AuthActionResult>;
  onEmailSignIn: (email: string, password: string) => Promise<AuthActionResult>;
  onEmailSignUp: (name: string, email: string, password: string) => Promise<AuthActionResult>;
  onCheckMethods: (email: string) => Promise<string[]>;
};
type AuthMode = "signin" | "signup";
function providerLabel(providerId: string) {
  switch (providerId) {
    case "google.com":
      return "Google";
    case "github.com":
      return "GitHub";
    case "facebook.com":
      return "Facebook";
    case "twitter.com":
      return "Twitter/X";
    case "microsoft.com":
      return "Microsoft";
    case "apple.com":
      return "Apple";
    default:
      return providerId;
  }
}
export function AuthPanel(t0) {
  const $ = _c(77);
  const {
    backendAwake,
    isBusy,
    onSocialSignIn,
    onEmailSignIn,
    onEmailSignUp,
    onCheckMethods
  } = t0;
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [methods, setMethods] = useState(t1);
  const [isCheckingMethods, setIsCheckingMethods] = useState(false);
  const [message, setMessage] = useState(null);
  let t2;
  let t3;
  if ($[1] !== email || $[2] !== onCheckMethods) {
    t2 = () => {
      const trimmed = email.trim();
      if (!trimmed) {
        return;
      }
      let cancelled = false;
      const timer = window.setTimeout(async () => {
        setIsCheckingMethods(true);
        const nextMethods = await onCheckMethods(trimmed);
        if (!cancelled) {
          setMethods(nextMethods);
          setIsCheckingMethods(false);
        }
      }, 280);
      return () => {
        cancelled = true;
        window.clearTimeout(timer);
      };
    };
    t3 = [email, onCheckMethods];
    $[1] = email;
    $[2] = onCheckMethods;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  bb0: {
    if (!methods.length || methods.includes("password")) {
      t4 = null;
      break bb0;
    }
    t4 = methods[0] ?? null;
  }
  const socialOnlyProvider = t4;
  let t5;
  bb1: {
    if (!socialOnlyProvider || !email.trim()) {
      t5 = null;
      break bb1;
    }
    let t6;
    if ($[5] !== socialOnlyProvider) {
      t6 = providerLabel(socialOnlyProvider);
      $[5] = socialOnlyProvider;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    const provider = t6;
    t5 = `This email is linked to ${provider}. Please continue with ${provider} sign-in instead of the form.`;
  }
  const providerBlockedHint = t5;
  const canSubmitForm = backendAwake && !isBusy && !socialOnlyProvider;
  let t6;
  if ($[7] !== onSocialSignIn) {
    t6 = async () => {
      setMessage(null);
      const result = await onSocialSignIn("google");
      if (!result.ok && result.error) {
        setMessage(result.error);
      }
    };
    $[7] = onSocialSignIn;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const runGoogleSignIn = t6;
  let t7;
  if ($[9] !== email || $[10] !== mode || $[11] !== name || $[12] !== onEmailSignIn || $[13] !== onEmailSignUp || $[14] !== password || $[15] !== providerBlockedHint) {
    t7 = async event => {
      event.preventDefault();
      setMessage(null);
      if (!email.trim() || !password.trim()) {
        setMessage("Please enter both email and password.");
        return;
      }
      if (mode === "signup" && password.trim().length < 6) {
        setMessage("Use at least 6 characters for your password.");
        return;
      }
      if (providerBlockedHint) {
        setMessage(providerBlockedHint);
        return;
      }
      const result_0 = mode === "signup" ? await onEmailSignUp(name, email, password) : await onEmailSignIn(email, password);
      if (!result_0.ok && result_0.error) {
        setMessage(result_0.error);
      }
    };
    $[9] = email;
    $[10] = mode;
    $[11] = name;
    $[12] = onEmailSignIn;
    $[13] = onEmailSignUp;
    $[14] = password;
    $[15] = providerBlockedHint;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const runEmailSubmit = t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      background: "var(--nt-glass-hi)",
      border: "1px solid var(--nt-glass-border)",
      backdropFilter: "blur(20px)",
      boxShadow: "0 32px 80px rgba(5, 15, 26, 0.24)"
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="text-[10px] uppercase tracking-[0.24em]" style={{
      color: "var(--nt-text-ghost)",
      fontFamily: "var(--font-jetbrains-mono)"
    }}>account access</span>;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <h2 className="text-2xl sm:text-3xl leading-tight" style={{
      color: "var(--nt-text-hi)",
      fontFamily: "var(--font-syne)",
      fontWeight: 700
    }}>Sign in to your Cognitive Workspace</h2>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="space-y-3">{t9}{t10}<p className="text-sm leading-relaxed" style={{
        color: "var(--nt-text-lo)"
      }}>Your reports stick to your account like glitter, so every insight is waiting for you when you come back, no memory gymnastics needed.</p></div>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== runGoogleSignIn) {
    t12 = () => void runGoogleSignIn();
    $[21] = runGoogleSignIn;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = !backendAwake || isBusy;
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      border: "1px solid var(--nt-divider)",
      color: "var(--nt-text-md)",
      background: "var(--nt-hover)"
    };
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t12 || $[25] !== t13) {
    t15 = <div className="flex flex-col justify-between gap-6">{t11}<div className="grid grid-cols-1 gap-2"><button type="button" onClick={t12} disabled={t13} className="rounded-xl px-3 py-2 text-xs font-medium transition-opacity hover:opacity-90 disabled:opacity-45" style={t14}>Continue with Google</button></div></div>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      border: "1px solid var(--nt-divider)",
      background: "var(--nt-glass)"
    };
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      background: "var(--nt-hover)"
    };
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setMode("signin");
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const t19 = mode === "signin" ? "var(--nt-btn-bg)" : "transparent";
  const t20 = mode === "signin" ? "var(--nt-btn-fg)" : "var(--nt-text-ghost)";
  let t21;
  if ($[30] !== t19 || $[31] !== t20) {
    t21 = <button type="button" onClick={t18} className="flex-1 rounded-md py-1.5 text-xs font-semibold uppercase tracking-wide" style={{
      background: t19,
      color: t20
    }}>Sign in</button>;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setMode("signup");
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  const t23 = mode === "signup" ? "var(--nt-btn-bg)" : "transparent";
  const t24 = mode === "signup" ? "var(--nt-btn-fg)" : "var(--nt-text-ghost)";
  let t25;
  if ($[34] !== t23 || $[35] !== t24) {
    t25 = <button type="button" onClick={t22} className="flex-1 rounded-md py-1.5 text-xs font-semibold uppercase tracking-wide" style={{
      background: t23,
      color: t24
    }}>Sign up</button>;
    $[34] = t23;
    $[35] = t24;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== t21 || $[38] !== t25) {
    t26 = <div className="mb-3 flex rounded-lg p-1" style={t17}>{t21}{t25}</div>;
    $[37] = t21;
    $[38] = t25;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== mode || $[41] !== name) {
    t27 = mode === "signup" && <input value={name} onChange={event_0 => setName(event_0.target.value)} placeholder="Full name" className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={{
      background: "var(--nt-hdr)",
      color: "var(--nt-text-hi)",
      border: "1px solid var(--nt-divider)"
    }} />;
    $[40] = mode;
    $[41] = name;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = event_1 => {
      const nextEmail = event_1.target.value;
      setEmail(nextEmail);
      if (!nextEmail.trim()) {
        setMethods([]);
        setIsCheckingMethods(false);
      }
    };
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      background: "var(--nt-hdr)",
      color: "var(--nt-text-hi)",
      border: "1px solid var(--nt-divider)"
    };
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] !== email) {
    t30 = <input value={email} onChange={t28} placeholder="Email address" type="email" autoComplete="email" className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={t29} />;
    $[45] = email;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  let t31;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = event_2 => setPassword(event_2.target.value);
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  const t32 = mode === "signup" ? "new-password" : "current-password";
  let t33;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      background: "var(--nt-hdr)",
      color: "var(--nt-text-hi)",
      border: "1px solid var(--nt-divider)"
    };
    $[48] = t33;
  } else {
    t33 = $[48];
  }
  let t34;
  if ($[49] !== password || $[50] !== t32) {
    t34 = <input value={password} onChange={t31} placeholder="Password" type="password" autoComplete={t32} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={t33} />;
    $[49] = password;
    $[50] = t32;
    $[51] = t34;
  } else {
    t34 = $[51];
  }
  let t35;
  if ($[52] !== isCheckingMethods) {
    t35 = isCheckingMethods && <p className="text-[11px]" style={{
      color: "var(--nt-text-ghost)"
    }}>Checking sign-in methods...</p>;
    $[52] = isCheckingMethods;
    $[53] = t35;
  } else {
    t35 = $[53];
  }
  let t36;
  if ($[54] !== providerBlockedHint) {
    t36 = providerBlockedHint && <p className="text-[11px] leading-relaxed" style={{
      color: "#D85A30"
    }}>{providerBlockedHint}</p>;
    $[54] = providerBlockedHint;
    $[55] = t36;
  } else {
    t36 = $[55];
  }
  let t37;
  if ($[56] !== message) {
    t37 = message && <p className="text-[11px] leading-relaxed" style={{
      color: "#D85A30"
    }}>{message}</p>;
    $[56] = message;
    $[57] = t37;
  } else {
    t37 = $[57];
  }
  const t38 = !canSubmitForm;
  let t39;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      background: "var(--nt-btn-bg)",
      color: "var(--nt-btn-fg)"
    };
    $[58] = t39;
  } else {
    t39 = $[58];
  }
  const t40 = mode === "signup" ? "Create account" : "Sign in";
  let t41;
  if ($[59] !== t38 || $[60] !== t40) {
    t41 = <button type="submit" disabled={t38} className="h-10 w-full rounded-xl text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-45" style={t39}>{t40}</button>;
    $[59] = t38;
    $[60] = t40;
    $[61] = t41;
  } else {
    t41 = $[61];
  }
  let t42;
  if ($[62] !== runEmailSubmit || $[63] !== t27 || $[64] !== t30 || $[65] !== t34 || $[66] !== t35 || $[67] !== t36 || $[68] !== t37 || $[69] !== t41) {
    t42 = <form className="space-y-3" onSubmit={runEmailSubmit}>{t27}{t30}{t34}{t35}{t36}{t37}{t41}</form>;
    $[62] = runEmailSubmit;
    $[63] = t27;
    $[64] = t30;
    $[65] = t34;
    $[66] = t35;
    $[67] = t36;
    $[68] = t37;
    $[69] = t41;
    $[70] = t42;
  } else {
    t42 = $[70];
  }
  let t43;
  if ($[71] !== t26 || $[72] !== t42) {
    t43 = <div className="rounded-2xl p-3 sm:p-4" style={t16}>{t26}{t42}</div>;
    $[71] = t26;
    $[72] = t42;
    $[73] = t43;
  } else {
    t43 = $[73];
  }
  let t44;
  if ($[74] !== t15 || $[75] !== t43) {
    t44 = <div className="w-full max-w-[980px] px-4 sm:px-6"><div className="grid gap-4 sm:gap-5 rounded-3xl p-4 sm:p-6 md:grid-cols-[1.15fr_1fr]" style={t8}>{t15}{t43}</div></div>;
    $[74] = t15;
    $[75] = t43;
    $[76] = t44;
  } else {
    t44 = $[76];
  }
  return t44;
}